import React, { FC, useState, useEffect, ChangeEvent, useRef } from 'react';
import axiosInstance from 'config/axiosInstance';
import {
  ChevronDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { DriverInterface } from 'components/Driver/DriverTable';
import user_icon from '../../../assets/icons/user.svg';
import add_driver_icon from '../../../assets/icons/add-driver.svg';
import { useTranslation } from 'react-i18next';

interface Client {
  id: number;
  name: string;
  assign_drivers: string[];
}

interface AssignDriverDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AssignDriverDialog: FC<AssignDriverDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('client');
  const [clients, setClients] = useState<Client[]>([]);
  const [drivers, setDrivers] = useState<DriverInterface[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [filteredDrivers, setFilteredDrivers] = useState<DriverInterface[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [clientSearchTerm, setClientSearchTerm] = useState<string>('');
  const [driverSearchTerm, setDriverSearchTerm] = useState<string>('');
  const [isClientDropdownVisible, setIsClientDropdownVisible] = useState(false);
  const [assignedDrivers, setAssignedDrivers] = useState<DriverInterface[]>([]);
  const [assignedDriversIds, setAssignedDriversIds] = useState<number[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchData = async () => {
    try {
      const clientsResponse = await axiosInstance.get(
        '/v1/client_deliveries/?page_size=0'
      );
      setClients(clientsResponse.data);

      const driversResponse = await axiosInstance.get(
        `/v1/drivers/?page_size=0`
      );
      setDrivers(driversResponse.data);
      setFilteredDrivers(driversResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handleClientChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setClientSearchTerm(searchValue);

    if (searchValue) {
      const filtered = clients.filter((client) =>
        client.name.toLowerCase().includes(searchValue)
      );
      setFilteredClients(filtered);
      setSelectedClientId(null);
    } else {
      setFilteredClients(clients);
      setSelectedClientId(null);
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setDriverSearchTerm(searchValue);

    if (searchValue) {
      const filtered = drivers.filter((driver) =>
        driver.name.toLowerCase().includes(searchValue)
      );
      setFilteredDrivers(filtered);
    } else {
      setFilteredDrivers(drivers);
    }
  };

  const selectClient = (clientId: number, clientName: string) => {
    setSelectedClientId(clientId);
    setClientSearchTerm(clientName);
    setIsClientDropdownVisible(false);
  };

  const handleDriverSelection = (driver: DriverInterface) => {
    if (assignedDrivers.find((d) => d.id === driver.id)) {
      setAssignedDrivers(assignedDrivers.filter((d) => d.id !== driver.id));
      setAssignedDriversIds(
        assignedDriversIds.filter((id) => id !== driver.id)
      );
    } else {
      setAssignedDrivers([...assignedDrivers, driver]);
      setAssignedDriversIds([...assignedDriversIds, driver.id]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const selectedClient = clients.find(
        (client) => client.id === selectedClientId
      );

      if (selectedClient) {
        const currentDrivers = selectedClient.assign_drivers || [];
        const updatedDrivers = Array.from(
          new Set([...currentDrivers, ...assignedDriversIds])
        );

        await axiosInstance.patch(
          `/v1/client_deliveries/${selectedClientId}/`,
          {
            assign_drivers: updatedDrivers,
          }
        );

        onClose();
        window.location.reload();
      } else {
        console.error('Selected client not found.');
      }
    } catch (error) {
      console.error('Error assigning client:', error);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      inputRef.current &&
      !inputRef.current.contains(event.target as Node)
    ) {
      if (!selectedClientId) {
        setClientSearchTerm('');
      }
      setIsClientDropdownVisible(false);
    }
  };

  const resetData = () => {
    setSelectedClientId(null);
    setClientSearchTerm('');
    setDriverSearchTerm('');
    setAssignedDrivers([]);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetData();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[768px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id="modal-headline"
                  >
                    {t('manage_client.assign_driver_title')}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form onSubmit={handleSubmit}>
                  <div className="px-6 pt-6">
                    <label className="block text-caption font-normal text-neutral/100">
                      {t('manage_client.client')}
                    </label>
                    <div className="relative">
                      <input
                        ref={inputRef}
                        type="text"
                        className="block w-full pl-4 pr-12 h-10 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
                        placeholder={t('manage_client.select_client')}
                        value={clientSearchTerm}
                        onChange={handleClientChange}
                        onFocus={() => {
                          setIsClientDropdownVisible(true);
                          setFilteredClients(clients);
                        }}
                      />
                      <ChevronDownIcon className="w-5 h-5 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none" />

                      {isClientDropdownVisible && (
                        <div
                          ref={dropdownRef}
                          className="absolute z-10 mt-1 w-full bg-white border border-neutral/50 rounded-md shadow-lg max-h-60 overflow-y-auto"
                        >
                          {filteredClients.length > 0 ? (
                            filteredClients.map((client) => (
                              <div
                                key={client.id}
                                className={`cursor-pointer px-4 py-2 hover:bg-gray-200 flex justify-between items-center ${
                                  selectedClientId === client.id
                                    ? 'text-primary/300 font-medium'
                                    : 'text-neutral/100'
                                }`}
                                onClick={() =>
                                  selectClient(client.id, client.name)
                                }
                              >
                                {client.name}
                                {selectedClientId === client.id && (
                                  <CheckIcon className="w-5 h-5 text-primary/300" />
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500">
                              No results found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6 px-6 pt-4 pb-6">
                    <div className="w-full">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={t('manage_client.search_driver')}
                          className="block w-full h-10 pl-4 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
                          value={driverSearchTerm}
                          onChange={handleSearchChange}
                        />
                        <MagnifyingGlassIcon className="w-4 h-4 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none" />
                      </div>
                      <div className="h-80 mt-4 overflow-y-scroll pr-4">
                        {filteredDrivers.map((driver) => (
                          <div
                            key={driver.id}
                            className="flex items-center justify-between py-2 border-b"
                          >
                            <div className="flex items-center">
                              <img
                                src={driver.profile_picture || user_icon}
                                alt="Driver"
                                className="h-10 w-10 object-cover rounded-full"
                              />
                              <div className="ml-4">
                                <p className="text-body-2 font-medium text-neutral/300">
                                  {driver.name}
                                </p>
                                <p className="text-caption text-neutral/75">
                                  {driver.phone}
                                </p>
                              </div>
                            </div>
                            {driver.client_delivery === null ? (
                              <>
                                {assignedDrivers.find(
                                  (d) => d.id === driver.id
                                ) ? (
                                  <div className="w-8 h-8 flex items-center justify-center">
                                    <CheckCircleIcon className="w-6 h-6 text-success" />
                                  </div>
                                ) : (
                                  <button
                                    className="w-8 h-8 bg-primary/50 rounded-md flex items-center justify-center"
                                    onClick={() =>
                                      handleDriverSelection(driver)
                                    }
                                    type="button"
                                  >
                                    <PlusIcon className="w-4 h-4 text-primary/300" />
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="text-caption text-neutral/100">
                                {t('manage_client.unavailable')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    {assignedDrivers.length > 0 ? (
                      <div className="text-left w-full">
                        <h6 className="text-subtitle-2 font-medium">
                          Total Assignments
                          <span className="font-regular">
                            {' - '}
                            {assignedDrivers.length}
                            {assignedDrivers.length > 1
                              ? ' drivers'
                              : ' driver'}
                          </span>
                        </h6>
                        <div className="mt-4 flex flex-wrap gap-2 overflow-y-scroll">
                          {assignedDrivers.map((driver) => (
                            <div
                              key={driver.id}
                              className="flex items-center gap-2 h-14 px-3 rounded-md border border-neutral/50"
                            >
                              <img
                                src={driver.profile_picture || user_icon}
                                alt="Driver"
                                className="h-10 w-10 object-cover rounded-full"
                              />
                              <span className="text-body-2 text-neutral/300">
                                {driver.name}
                              </span>
                              <XMarkIcon
                                className="w-6 h-6 cursor-pointer text-primary/300"
                                onClick={() => handleDriverSelection(driver)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full text-center flex flex-col gap-3 justify-center">
                        <img
                          src={add_driver_icon}
                          alt="Assign Driver"
                          className="h-14"
                        />
                        <h6 className="text-subtitle-2 font-medium">
                          {t('manage_client.assign_driver_title')}
                        </h6>
                        <p className="text-caption text-neutral/75">
                          {t('manage_client.assign_driver_subtitle_1')}
                          <br /> {t('manage_client.assign_driver_subtitle_2')}
                          <br /> {t('manage_client.assign_driver_subtitle_3')}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12"
                    >
                      {t('action.assign')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignDriverDialog;
